import type { ActionSource } from './schema';
import getAsset from './selectors/getAsset';
import getProject from './selectors/getProject';
import getDueDateActionType from './utils/getDueDateActionType';
import { toJS } from 'mobx';
import { actionWithDatapoint } from 'owa-analytics-actions';
import type { OwaDate } from 'owa-datetime';
import type { NotificationBarHostId } from 'owa-notification-bar';
import { action } from 'satcheljs';
import type {
    AssetId,
    NewOutlookSpaceAsset,
    OutlookSpace,
    OutlookSpaceAsset,
    PatchOutlookSpace,
    PatchOutlookSpaceAsset,
    ProjectId,
} from 'owa-timestream-types';
import { GridSize } from './constants';

const withProjectId = (projectId: ProjectId) => ({
    projectId,
});

const timeStreamProjectDatapoint = ({ projectId }: { projectId: ProjectId }) => ({
    customData: {
        projectId: projectId,
        template_1: getProject(projectId).templateType,
    },
    options: { isCore: true },
});

export const loadSummaries = actionWithDatapoint(
    'OUTLOOKSPACES_LOAD_SUMMARIES',
    (appName?: string) => ({ appName }),
    () => ({
        options: { isCore: true },
    })
);

export const loadProject = actionWithDatapoint('OUTLOOKSPACES_LOAD_PROJECT', withProjectId, () => ({
    options: { isCore: true },
}));

export const deleteTimeStreamProject = actionWithDatapoint(
    'OUTLOOKSPACES_DELETE_PROJECT',
    withProjectId,
    timeStreamProjectDatapoint
);

export const openTimeStreamProject = actionWithDatapoint(
    'OUTLOOKSPACES_OPEN_PROJECT_ACTION',
    (projectId: ProjectId) => ({ projectId }),
    () => ({ options: { isCore: true } })
);

export const closeCurrentTimeStreamProject = actionWithDatapoint(
    'OUTLOOKSPACES_CLOSE_PROJECT',
    () => ({}),
    () => ({ options: { isCore: true } })
);

export const timeStreamProjectPreviewOpened = actionWithDatapoint(
    'OUTLOOKSPACES_PROJECT_PREVIEW',
    (outlookSpace: OutlookSpace, assets: OutlookSpaceAsset[]) => ({
        outlookSpace,
        assets,
    }),
    ({ outlookSpace: { id, templateType } }) => ({
        customData: {
            projectId: id,
            template_1: templateType,
        },
        options: { isCore: true },
    })
);

export const timeStreamProjectCreated = actionWithDatapoint(
    'OUTLOOKSPACES_PROJECT_CREATE',
    (targetMailbox: string, outlookSpace: OutlookSpace) => ({ targetMailbox, outlookSpace }),
    ({ targetMailbox, outlookSpace: { id, templateType } }) => ({
        customData: {
            projectId: id,
            template_1: templateType,
            hasTargetMailbox_2: !!targetMailbox,
        },
        options: { isCore: true },
    })
);

export const timeStreamProjectUpdated = actionWithDatapoint(
    'OUTLOOKSPACES_PROJECT_UPDATE',
    (projectId: ProjectId, outlookSpace: PatchOutlookSpace, preventSearch?: boolean) => ({
        projectId,
        outlookSpace,
        preventSearch,
    }),
    timeStreamProjectDatapoint
);

export const createTimeStreamAsset = actionWithDatapoint(
    'OUTLOOKSPACES_CREATE_ASSET',
    (projectId: ProjectId, asset: NewOutlookSpaceAsset, source: ActionSource) => ({
        projectId,
        asset,
        source,
    }),
    ({ asset: { type, x, y }, source }) => ({
        customData: { assetType_1: type, source_2: source, x, y },
        isCore: true,
    })
);

export const onTimeStreamAssetCreated = action(
    'OUTLOOKSPACES_ASSET_CREATED',
    (projectId: ProjectId, assetId: AssetId, source: ActionSource) => ({
        projectId,
        assetId,
        source,
    })
);

export const moveTimeStreamAsset = actionWithDatapoint(
    'OUTLOOKSPACES_MOVE_ASSET',
    (projectId: ProjectId, assetId: AssetId, mouse_dx: number, mouse_dy: number) => {
        const { type, bounds, isCaptured } = getAsset(projectId, assetId);
        const x = snapToGrid(bounds.x + mouse_dx);
        const y = snapToGrid(bounds.y + mouse_dy);
        const dx = x - bounds.x;
        const dy = y - bounds.y;
        const partialOriginalAsset = {
            bounds: toJS(bounds),
            isCaptured,
        };
        return {
            projectId,
            assetType: type,
            assetId,
            mouse_dx,
            mouse_dy,
            dx,
            dy,
            x,
            y,
            partialOriginalAsset: partialOriginalAsset,
        };
    },
    ({ assetType, mouse_dx, mouse_dy, dx, dy, x, y }) => ({
        customData: { assetType_1: assetType, mouse_dx, mouse_dy, dx, dy, x, y },
        isCore: true,
    })
);

export const moveTimeStreamAssetToPosition = action(
    'OUTLOOKSPACES_MOVE_ASSET_TO_POSITION',
    (projectId: ProjectId, assetId: AssetId, x?: number, y?: number, onlySetLocally?: boolean) => {
        const { type, bounds, isCaptured } = getAsset(projectId, assetId);

        const partialOriginalAsset = {
            bounds: toJS(bounds),
            isCaptured,
        };
        const newX = x !== undefined ? snapToGrid(x) : bounds.x;
        const newY = y !== undefined ? snapToGrid(y) : bounds.y;

        return {
            projectId,
            assetType: type,
            assetId,
            x: newX,
            y: newY,
            partialOriginalAsset: partialOriginalAsset,
            onlySetLocally,
        };
    }
);

export function snapToGrid(position: number) {
    return Math.floor(position / GridSize) * GridSize;
}

export const setDueDate = actionWithDatapoint(
    'OUTLOOKSPACES_SET_DUE_DATE',
    (
        projectId: ProjectId,
        assetId: AssetId,
        dueDate: OwaDate | null,
        shouldRemoveTask?: boolean
    ) => {
        const asset = getAsset(projectId, assetId);
        const assetType = asset.type;
        const oldDueDate = asset.dueDate;
        return {
            projectId,
            assetId,
            dueDate,
            oldDueDate,
            assetType,
            shouldRemoveTask,
        };
    },
    ({ assetType, dueDate, oldDueDate }) => ({
        customData: {
            assetType_1: assetType,
            actionType_2: getDueDateActionType(dueDate, oldDueDate),
        },
    })
);

export const setIsComplete = action(
    'OUTLOOKSPACES_SET_IS_COMPLETE',
    (projectId: ProjectId, assetId: AssetId, isComplete: boolean) => {
        const wasPreviouslyComplete = getAsset(projectId, assetId).isComplete;
        return {
            projectId,
            assetId,
            isComplete,
            wasPreviouslyComplete,
        };
    }
);

export const setTaskId = action(
    'OUTLOOKSPACES_SET_TASK_ID',
    (
        projectId: ProjectId,
        assetId: AssetId,
        taskId: string,
        taskDueDate?: OwaDate,
        onlySetLocally?: boolean
    ) => {
        const asset = getAsset(projectId, assetId);
        return {
            projectId,
            assetId,
            taskId,
            oldTaskId: asset.taskId,
            taskDueDate,
            oldDueDate: asset.dueDate,
            onlySetLocally,
        };
    }
);

export const setColor = actionWithDatapoint(
    'OUTLOOKSPACES_SET_COLOR',
    (projectId: ProjectId, assetId: AssetId, color: string) => ({ projectId, assetId, color }),
    ({ color }) => ({ customData: { color_1: color } })
);

export const deleteAssets = actionWithDatapoint(
    'OUTLOOKSPACES_DELETE_ASSETS',
    (projectId: ProjectId, assetIds: AssetId[]) => ({ projectId, assetIds }),
    (_, projectId, assetIds) => ({
        customData: assetIds.reduce(
            (a, assetId) => {
                const { type } = getAsset(projectId, assetId);
                a[type] = (a[type] || 0) + 1;
                return a;
            },
            { selectedCount_1: assetIds.length }
        ),
    })
);

export const onTimeStreamAssetsDeleted = action(
    'OUTLOOKSPACES_ASSETS_DELETED',
    (assetIds: AssetId[]) => ({ assetIds })
);

export const patchAssetPayload = actionWithDatapoint(
    'OUTLOOKSPACES_PATCH_ASSET_PAYLOAD',
    (
        projectId: ProjectId,
        assetId: AssetId,
        isPartialPayload: boolean,
        payload: unknown,
        newTaskId?: Pick<PatchOutlookSpaceAsset, 'taskId'>
    ) => {
        const asset = getAsset(projectId, assetId);
        const currentPayload = asset.payload;
        return {
            projectId,
            assetId,
            payload,
            newTaskId,
            isPartialPayload,
            payloadId: Date.now() * 1000 + Math.floor(Math.random() * 1000),
            currentPayload,
            partialOriginalAsset: {
                payload: toJS(currentPayload),
                ...(newTaskId
                    ? Object.keys(newTaskId).reduce((obj, key) => {
                          obj[key] = asset[key];
                          return obj;
                      }, {} as Record<keyof Pick<PatchOutlookSpaceAsset, 'taskId'>, any>)
                    : {}),
            },
        };
    },
    (_, projectId, assetId, newTaskId) => ({
        customData: {
            assetType_1: getAsset(projectId, assetId).type,
            isUpdatingTaskid: !!newTaskId,
        },
    })
);

export const updateAssetParent = actionWithDatapoint(
    'OUTLOOKSPACES_UPDATE_ASSET_PARENT',
    (
        projectId: ProjectId,
        assetId: AssetId,
        parentId: AssetId | null,
        order: number | undefined,
        reorderChildren: boolean = false,
        x?: number,
        y?: number
    ) => ({
        projectId,
        assetId,
        parentId,
        order,
        reorderChildren,
        x,
        y,
        parentUpdateId: Date.now() * 1000 + Math.floor(Math.random() * 1000),
        partialOriginalAsset: {
            payload: {
                parentId: getAsset(projectId, assetId).parentId,
                order: getAsset(projectId, assetId).order,
            },
        },
    }),
    (_, projectId, assetId) => ({
        customData: { assetType_1: getAsset(projectId, assetId).type },
    })
);

export const reorderAssetChildren = action(
    'reorderAssetChildren',
    (projectId: ProjectId, assetId: AssetId) => ({
        projectId,
        assetId,
    })
);

export const removeBucketChild = action(
    'removeBucketChild',
    (projectId: ProjectId, assetId: AssetId, dragIntoBucket: boolean) => ({
        projectId,
        assetId,
        dragIntoBucket,
    })
);

export const trackAssetSize = action(
    'trackAssetSize',
    (projectId: ProjectId, assetId: AssetId, width: number, height: number) => ({
        projectId,
        assetId,
        width,
        height,
    })
);

export const updateAssetSize = action(
    'updateAssetSize',
    (
        projectId: ProjectId,
        assetId: AssetId,
        width: number,
        height: number,
        x: number,
        y: number
    ) => {
        const { bounds } = getAsset(projectId, assetId);
        const partialOriginalAsset = {
            bounds: toJS(bounds),
        };

        return {
            projectId,
            assetId,
            width,
            height,
            x: snapToGrid(x),
            y: snapToGrid(y),
            partialOriginalAsset,
        };
    }
);

export const resetIsNewlyCreated = action(
    'resetIsNewlyCreated',
    (projectId: ProjectId, assetId: AssetId) => ({
        projectId,
        assetId,
    })
);

export const resetProjectUnseenCapturedCount = action(
    'resetSpaceUnseenCapturedCount',
    (id: ProjectId) => ({
        id,
    })
);

export const setNotificationBarHostId = action(
    'setNotificationBarHostId',
    (hostId: NotificationBarHostId) => ({
        hostId,
    })
);

const withAnnouncement = (announcement: string) => ({ announcement });
export const setAnnouncement = action('setAnnouncement', withAnnouncement);
export const setPoliteAnnouncement = action('setPoliteAnnouncement', withAnnouncement);

export const onAssetsLoaded = action('OUTLOOKSPACES_ASSETS_LOADED', (projectId: ProjectId) => ({
    projectId,
    validAssetIds: [...getProject(projectId).assets.keys()].reduce(
        (a: { [key: string]: true }, k: string) => ((a[k] = true), a),
        {}
    ),
    outlookSpaceAssets: [...getProject(projectId).assets.values()],
}));
